import { useState, useEffect } from 'react'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Plus,
  User,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  MoreHorizontal,
  Pencil,
  UserX,
  Trash2,
} from 'lucide-react'

import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import { useActivePatient } from '@context/ActivePatientContext'
import { useToast } from '@hooks/use-toast'
import { appointmentApi } from '@services/appointment.service'
import { useAuthStore } from '@hooks/useAuthStore'
import { AppointmentCreate, Appointment } from '@/types'
import { cn } from '@lib/utils'
import { getApiErrorMessage } from '@services/api'
import {
  getConsultationsByPatient,
  type ConsultationSummary,
} from '@services/consultation.service'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu'

type FilterStatus = 'all' | 'scheduled' | 'completed' | 'no_show' | 'cancelled'

const filterPills: { key: FilterStatus; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'scheduled', label: 'Programadas' },
  { key: 'completed', label: 'Completadas' },
  { key: 'no_show', label: 'No asistió' },
  { key: 'cancelled', label: 'Canceladas' },
]

const statusConfig: Record<
  string,
  { label: string; bg: string; text: string; icon: typeof Clock }
> = {
  scheduled: {
    label: 'Programada',
    bg: 'bg-ml-secondary-container',
    text: 'text-ml-primary',
    icon: Clock,
  },
  confirmed: {
    label: 'Confirmada',
    bg: 'bg-sky-100',
    text: 'text-sky-700',
    icon: CheckCircle2,
  },
  checked_in: {
    label: 'En recepción',
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    icon: User,
  },
  in_progress: {
    label: 'En curso',
    bg: 'bg-ml-primary-fixed-dim/30',
    text: 'text-ml-primary',
    icon: CheckCircle2,
  },
  completed: {
    label: 'Completada',
    bg: 'bg-ml-primary-fixed',
    text: 'text-ml-primary',
    icon: CheckCircle2,
  },
  cancelled: {
    label: 'Cancelada',
    bg: 'bg-ml-error-container/50',
    text: 'text-ml-error',
    icon: XCircle,
  },
  no_show: {
    label: 'No asistió',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    icon: AlertCircle,
  },
  rescheduled: {
    label: 'Reprogramada',
    bg: 'bg-violet-100',
    text: 'text-violet-700',
    icon: Clock,
  },
}

const appointmentStatusLabels: Record<string, string> = {
  scheduled: 'Programada',
  confirmed: 'Confirmada',
  checked_in: 'En recepción',
  in_progress: 'En curso',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
  rescheduled: 'Reprogramada',
}

const appointmentTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  follow_up: 'Seguimiento',
  emergency: 'Emergencia',
  routine_checkup: 'Revisión rutinaria',
  vaccination: 'Vacunación',
  lab_test: 'Laboratorio',
  imaging: 'Imagen',
  procedure: 'Procedimiento',
  telemedicine: 'Telemedicina',
}

export function HistorialCitasTab() {
  const { activePatientData } = useActivePatient()
  const { toast } = useToast()
  const { user: currentUser } = useAuthStore()
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<FilterStatus>('all')
  const [slotDate, setSlotDate] = useState('')
  const [availableSlots, setAvailableSlots] = useState<
    { start_at: string; end_at: string }[]
  >([])
  const [slotsTimezone, setSlotsTimezone] = useState<string>('UTC')
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState<string | null>(null)
  const [completedConsultations, setCompletedConsultations] = useState<ConsultationSummary[]>([])

  const isDoctor =
    currentUser?.role === 'doctor' ||
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin'

  const [formData, setFormData] = useState<AppointmentCreate>({
    patient_id: activePatientData?.id || '',
    doctor_id: isDoctor ? currentUser?.id || '' : '',
    appointment_date: '',
    duration_minutes: 30,
    appointment_type: 'consultation',
    origin_consultation_id: null,
    reason: '',
    notes: '',
    department: currentUser?.department || '',
    room_number: '',
    is_telemedicine: false,
  })

  useEffect(() => {
    if (activePatientData?.id) {
      loadAppointments()
    }
  }, [activePatientData?.id])

  useEffect(() => {
    if (!isCreateOpen || !slotDate || !formData.doctor_id) return
    loadAvailableSlots(formData.doctor_id, slotDate)
  }, [isCreateOpen, slotDate, formData.doctor_id])

  const loadAppointments = async () => {
    if (!activePatientData?.id) return
    setLoading(true)
    setError(null)
    try {
      const [appointmentResponse, patientConsultations] = await Promise.all([
        appointmentApi.getAppointments({
          patient_id: activePatientData.id,
          limit: 100,
        }),
        getConsultationsByPatient(activePatientData.id, 100).catch(() => [] as ConsultationSummary[]),
      ])
      setAppointments(appointmentResponse.items)
      setCompletedConsultations(
        patientConsultations.filter((consultation) => consultation.status === 'completed')
      )
    } catch (err) {
      console.error('Failed to load appointments:', err)
      setError('No se pudieron cargar las citas')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (apt: Appointment) => {
    setSelectedAppointment(apt)
    setFormData({
      patient_id: apt.patient_id,
      doctor_id: apt.doctor_id,
      appointment_date: apt.appointment_date.slice(0, 16),
      duration_minutes: apt.duration_minutes,
      appointment_type: apt.appointment_type,
      origin_consultation_id: apt.origin_consultation_id || null,
      reason: apt.reason,
      notes: apt.notes || '',
      department: apt.department || '',
      room_number: apt.room_number || '',
      is_telemedicine: apt.is_telemedicine,
    })
    setIsEditOpen(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedAppointment) return

    if (
      formData.appointment_type === 'follow_up' &&
      !formData.origin_consultation_id
    ) {
      toast({
        title: 'Falta la consulta origen',
        description:
          'Selecciona la consulta completada que origina este seguimiento antes de guardar la cita.',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)
    try {
      await appointmentApi.updateAppointment(selectedAppointment.id, formData)
      toast({ title: 'Exito', description: 'Cita actualizada exitosamente' })
      setIsEditOpen(false)
      setSelectedAppointment(null)
      resetForm()
      loadAppointments()
    } catch (err) {
      console.error('Failed to update appointment:', err)
      toast({
        title: 'Error',
        description: getApiErrorMessage(err as any, 'No se pudo actualizar la cita.'),
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStatusChange = async (apt: Appointment, status: string) => {
    try {
      await appointmentApi.updateAppointment(apt.id, { status })
      toast({ title: 'Éxito', description: `Cita marcada como ${appointmentStatusLabels[status] ?? status}` })
      loadAppointments()
    } catch (err) {
      console.error('Failed to update appointment status:', err)
      toast({
        title: 'Error',
        description: getApiErrorMessage(
          err as any,
          'No se pudo actualizar el estado de la cita.'
        ),
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async (apt: Appointment) => {
    if (!confirm('Esta seguro de que desea eliminar esta cita?')) return
    try {
      await appointmentApi.deleteAppointment(apt.id)
      toast({ title: 'Exito', description: 'Cita eliminada exitosamente' })
      loadAppointments()
    } catch (err) {
      console.error('Failed to delete appointment:', err)
      toast({
        title: 'Error',
        description: getApiErrorMessage(err as any, 'No se pudo eliminar la cita.'),
        variant: 'destructive',
      })
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activePatientData?.id) return
    if (!formData.doctor_id?.trim()) {
      toast({
        title: 'Error de Validacion',
        description: 'Por favor ingresa un ID de Doctor',
        variant: 'destructive',
      })
      return
    }
    if (!formData.appointment_date) {
      toast({
        title: 'Error de Validacion',
        description: 'Por favor selecciona un horario disponible',
        variant: 'destructive',
      })
      return
    }
    if (!formData.reason?.trim()) {
      toast({
        title: 'Error de Validacion',
        description: 'Por favor ingresa un motivo para la cita',
        variant: 'destructive',
      })
      return
    }
    if (
      formData.appointment_type === 'follow_up' &&
      !formData.origin_consultation_id
    ) {
      toast({
        title: 'Falta la consulta origen',
        description:
          'Selecciona la consulta completada que origina este seguimiento antes de guardar la cita.',
        variant: 'destructive',
      })
      return
    }
    setIsSubmitting(true)
    try {
      await appointmentApi.createAppointment({
        ...formData,
        patient_id: activePatientData.id,
      })
      toast({ title: 'Exito', description: 'Cita creada exitosamente' })
      setIsCreateOpen(false)
      resetForm()
      loadAppointments()
    } catch (err: any) {
      console.error('Failed to create appointment:', err)
      toast({
        title: 'Error',
        description: getApiErrorMessage(err, 'No se pudo crear la cita.'),
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      patient_id: activePatientData?.id || '',
      doctor_id: isDoctor ? currentUser?.id || '' : '',
      appointment_date: '',
      duration_minutes: 30,
      appointment_type: 'consultation',
      origin_consultation_id: null,
      reason: '',
      notes: '',
      department: currentUser?.department || '',
      room_number: '',
      is_telemedicine: false,
    })
    setSlotDate('')
    setAvailableSlots([])
    setSlotsTimezone('UTC')
    setSlotsError(null)
  }

  const loadAvailableSlots = async (doctorId: string, dateValue: string) => {
    if (!doctorId || !dateValue) return
    setSlotsLoading(true)
    setSlotsError(null)
    try {
      const response = await appointmentApi.getAvailableSlots(
        doctorId,
        dateValue,
        dateValue
      )
      setAvailableSlots(response.slots)
      setSlotsTimezone(response.timezone || 'UTC')
      setFormData((prev) => {
        const stillValid = response.slots.some(
          (slot) => slot.start_at === prev.appointment_date
        )
        return {
          ...prev,
          duration_minutes: response.slot_minutes,
          appointment_date: stillValid ? prev.appointment_date : '',
        }
      })
    } catch (err: any) {
      console.error('Failed to load slots:', err)
      setAvailableSlots([])
      setSlotsError(
        err?.response?.data?.detail ||
          'No se pudieron cargar los horarios disponibles'
      )
    } finally {
      setSlotsLoading(false)
    }
  }

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return {
      date: date.toLocaleDateString('es-MX', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' }),
      day: date.getDate(),
      month: date.toLocaleDateString('es-MX', { month: 'short' }),
      fullDate: date.toLocaleDateString('es-MX', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    }
  }

  if (!activePatientData) return null

  const isFollowUp = formData.appointment_type === 'follow_up'
  const hasCompletedConsultations = completedConsultations.length > 0

  const formatConsultationOption = (consultation: ConsultationSummary) => {
    const dateLabel = new Date(consultation.created_at).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    const diagnosisLabel =
      consultation.diagnosis_codes.length > 0
        ? consultation.diagnosis_codes.slice(0, 2).join(', ')
        : 'Sin diagnósticos capturados'

    return `${dateLabel} · ${diagnosisLabel}`
  }

  const filteredAppointments =
    activeFilter === 'all'
      ? appointments
      : appointments.filter((a) => a.status === activeFilter)

  // Sort reverse-chronological
  const sortedAppointments = [...filteredAppointments].sort(
    (a, b) =>
      new Date(b.appointment_date).getTime() - new Date(a.appointment_date).getTime()
  )

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-headline italic text-ml-primary">
            Historial de Citas
          </h2>
          <p className="text-sm text-ml-on-surface-variant">
            Historial y programacion de citas de {activePatientData.full_name}
          </p>
        </div>

        {/* Create Dialog */}
        <Dialog
          open={isCreateOpen}
          onOpenChange={(open) => {
            setIsCreateOpen(open)
            if (!open) return
            const now = new Date()
            const initialDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
            setSlotDate(initialDate)
            setFormData((prev) => ({ ...prev, appointment_date: '' }))
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-ml-primary text-white hover:bg-ml-primary-container rounded px-6 shadow-lg shadow-ml-primary/10">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Cita
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Programar Cita</DialogTitle>
              <DialogDescription>
                Crear una nueva cita para {activePatientData.full_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="appointment_date">Fecha de Cita *</Label>
                  <Input
                    id="appointment_date"
                    type="date"
                    value={slotDate}
                    onChange={(e) => {
                      setSlotDate(e.target.value)
                      setFormData({ ...formData, appointment_date: '' })
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duracion (minutos)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min={15}
                    step={15}
                    value={formData.duration_minutes}
                    readOnly
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="doctor_id">Doctor *</Label>
                {isDoctor && currentUser ? (
                  <div className="flex items-center gap-2 p-2 rounded-md border border-input bg-muted/50">
                    <User className="h-4 w-4 text-medical-600" />
                    <span className="text-sm font-medium">
                      Dr. {currentUser.first_name} {currentUser.last_name}
                    </span>
                    <span className="text-xs text-muted-foreground">(Tu)</span>
                  </div>
                ) : (
                  <Input
                    id="doctor_id"
                    value={formData.doctor_id}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        doctor_id: e.target.value,
                        appointment_date: '',
                      })
                      setAvailableSlots([])
                    }}
                    placeholder="UUID del doctor"
                    required
                    pattern="[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}"
                    title="Por favor ingresa un UUID valido"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slot_pick">Horarios Disponibles *</Label>
                <Select
                  value={formData.appointment_date}
                  onValueChange={(value) =>
                    setFormData({ ...formData, appointment_date: value })
                  }
                  disabled={slotsLoading || !slotDate || !formData.doctor_id}
                >
                  <SelectTrigger id="slot_pick" className="w-full">
                    <SelectValue
                      placeholder={
                        slotsLoading
                          ? 'Cargando horarios...'
                          : 'Seleccionar un horario disponible'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSlots.map((slot) => (
                      <SelectItem key={slot.start_at} value={slot.start_at}>
                        {new Date(slot.start_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: slotsTimezone,
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {slotsError && <p className="text-xs text-red-600">{slotsError}</p>}
                {!slotsError &&
                  !slotsLoading &&
                  !!slotDate &&
                  !!formData.doctor_id &&
                  availableSlots.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No hay horarios disponibles para esta fecha.
                    </p>
                  )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="appointment_type">Tipo de cita</Label>
                <select
                  id="appointment_type"
                  value={formData.appointment_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      appointment_type: e.target.value,
                      origin_consultation_id:
                        e.target.value === 'follow_up'
                          ? formData.origin_consultation_id
                          : null,
                    })
                  }
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="consultation">Consulta</option>
                  <option value="follow_up">Seguimiento</option>
                  <option value="emergency">Emergencia</option>
                  <option value="routine_checkup">Chequeo de rutina</option>
                  <option value="vaccination">Vacunación</option>
                  <option value="lab_test">Prueba de laboratorio</option>
                  <option value="imaging">Imagenología</option>
                  <option value="procedure">Procedimiento</option>
                  <option value="telemedicine">Telemedicina</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Usa <strong>Seguimiento</strong> solo cuando ya exista una consulta inicial completada del mismo paciente.
                </p>
              </div>

              {isFollowUp && (
                <div className="space-y-2">
                  <Label htmlFor="origin_consultation_id">Consulta origen *</Label>
                  <Select
                    value={formData.origin_consultation_id ?? ''}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        origin_consultation_id: value || null,
                      })
                    }
                    disabled={!hasCompletedConsultations}
                  >
                    <SelectTrigger id="origin_consultation_id" className="w-full">
                      <SelectValue
                        placeholder={
                          hasCompletedConsultations
                            ? 'Selecciona la consulta previa'
                            : 'No hay consultas completadas disponibles'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {completedConsultations.map((consultation) => (
                        <SelectItem key={consultation.id} value={consultation.id}>
                          {formatConsultationOption(consultation)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    El backend validará que esta consulta esté completada y pertenezca al mismo paciente y clínica.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="reason">Motivo *</Label>
                <Input
                  id="reason"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                  placeholder="Motivo de la cita"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Departamento</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) =>
                    setFormData({ ...formData, department: e.target.value })
                  }
                  placeholder="Departamento"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Notas adicionales"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-ml-primary text-white hover:bg-ml-primary-container"
                  disabled={
                    isSubmitting ||
                    !formData.appointment_date ||
                    (isFollowUp && !formData.origin_consultation_id)
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    'Programar Cita'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Editar Cita</DialogTitle>
              <DialogDescription>
                Actualizar detalles de cita de {activePatientData?.first_name}{' '}
                {activePatientData?.last_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_appointment_date">Fecha y Hora *</Label>
                  <Input
                    id="edit_appointment_date"
                    type="datetime-local"
                    value={formData.appointment_date}
                    onChange={(e) =>
                      setFormData({ ...formData, appointment_date: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_duration">Duracion (minutos)</Label>
                  <Input
                    id="edit_duration"
                    type="number"
                    min={15}
                    step={15}
                    value={formData.duration_minutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        duration_minutes: parseInt(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_appointment_type">Tipo de Cita</Label>
                <select
                  id="edit_appointment_type"
                  value={formData.appointment_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      appointment_type: e.target.value,
                      origin_consultation_id:
                        e.target.value === 'follow_up'
                          ? formData.origin_consultation_id
                          : null,
                    })
                  }
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="consultation">Consulta</option>
                  <option value="follow_up">Seguimiento</option>
                  <option value="emergency">Emergencia</option>
                  <option value="routine_checkup">Chequeo de Rutina</option>
                  <option value="vaccination">Vacunación</option>
                  <option value="lab_test">Prueba de Laboratorio</option>
                  <option value="imaging">Imagenologia</option>
                  <option value="procedure">Procedimiento</option>
                  <option value="telemedicine">Telemedicina</option>
                </select>
              </div>

              {isFollowUp && (
                <div className="space-y-2">
                  <Label htmlFor="edit_origin_consultation_id">Consulta origen *</Label>
                  <Select
                    value={formData.origin_consultation_id ?? ''}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        origin_consultation_id: value || null,
                      })
                    }
                    disabled={!hasCompletedConsultations}
                  >
                    <SelectTrigger id="edit_origin_consultation_id" className="w-full">
                      <SelectValue
                        placeholder={
                          hasCompletedConsultations
                            ? 'Selecciona la consulta previa'
                            : 'No hay consultas completadas disponibles'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {completedConsultations.map((consultation) => (
                        <SelectItem key={consultation.id} value={consultation.id}>
                          {formatConsultationOption(consultation)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit_reason">Motivo *</Label>
                <Input
                  id="edit_reason"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                  placeholder="Motivo de la cita"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_notes">Notas</Label>
                <Textarea
                  id="edit_notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Notas adicionales"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-ml-primary text-white hover:bg-ml-primary-container"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    'Guardar Cambios'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {filterPills.map((pill) => (
          <button
            key={pill.key}
            onClick={() => setActiveFilter(pill.key)}
            className={cn(
              'px-4 py-2 text-xs font-bold rounded whitespace-nowrap transition-colors uppercase tracking-wider',
              activeFilter === pill.key
                ? 'bg-ml-primary text-white'
                : 'bg-ml-surface-container-high text-ml-on-surface-variant hover:bg-ml-surface-container-highest'
            )}
          >
            {pill.label}
          </button>
        ))}
      </div>

      {/* Appointments List */}
      <div className="bg-ml-surface-container-lowest rounded-2xl border border-ml-outline-variant/5">
        <div className="px-8 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-ml-primary" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-ml-error/40 mx-auto mb-4" />
              <h3 className="text-lg font-headline italic text-ml-primary mb-1">
                Error al Cargar Citas
              </h3>
              <p className="text-ml-on-surface-variant">{error}</p>
              <Button
                onClick={loadAppointments}
                variant="outline"
                className="mt-4 rounded"
              >
                Reintentar
              </Button>
            </div>
          ) : sortedAppointments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 text-ml-on-surface-variant/20 mx-auto mb-4" />
              <h3 className="text-lg font-headline italic text-ml-primary mb-1">
                Sin Citas
              </h3>
              <p className="text-ml-on-surface-variant mb-4">
                {activeFilter === 'all'
                  ? 'Programa una cita para comenzar.'
                  : 'No hay citas con este filtro.'}
              </p>
              {activeFilter === 'all' && (
                <Button
                  onClick={() => setIsCreateOpen(true)}
                  className="bg-ml-primary text-white rounded px-6"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Programar Primera Cita
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {sortedAppointments.map((apt) => {
                const status = statusConfig[apt.status] || statusConfig.scheduled
                const StatusIcon = status.icon
                const dateTime = formatDateTime(apt.appointment_date)
                const isExpanded = expandedId === apt.id

                return (
                  <div
                    key={apt.id}
                    className="rounded-xl border border-ml-outline-variant/5 overflow-hidden"
                  >
                    {/* Card header */}
                    <div
                      className="flex items-center gap-4 p-4 hover:bg-ml-surface-container-low transition-colors cursor-pointer"
                      onClick={() =>
                        setExpandedId(isExpanded ? null : apt.id)
                      }
                    >
                      {/* Date box */}
                      <div className="flex-shrink-0 w-14 text-center">
                        <p className="text-xs text-ml-on-surface-variant uppercase font-bold tracking-widest">
                          {dateTime.month}
                        </p>
                        <p className="text-2xl font-headline text-ml-primary">
                          {dateTime.day}
                        </p>
                      </div>

                      <div className="w-px h-12 bg-ml-outline-variant/20" />

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-sm text-ml-primary">
                            {appointmentTypeLabels[apt.appointment_type] ||
                              apt.appointment_type ||
                              'Cita'}
                          </p>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                              status.bg,
                              status.text
                            )}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-ml-on-surface-variant">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {dateTime.time} ({apt.duration_minutes} min)
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            {apt.doctor
                              ? `${apt.doctor.first_name} ${apt.doctor.last_name}`
                              : 'Doctor'}
                          </span>
                        </div>
                      </div>

                      {/* Expand + Actions */}
                      <div className="flex items-center gap-1">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-ml-on-surface-variant" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-ml-on-surface-variant" />
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(apt)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {apt.status === 'scheduled' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChange(apt, 'confirmed')
                                  }
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2 text-sky-600" />
                                  Confirmar cita
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChange(apt, 'checked_in')
                                  }
                                >
                                  <User className="h-4 w-4 mr-2 text-indigo-600" />
                                  Registrar llegada
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChange(apt, 'no_show')
                                  }
                                >
                                  <UserX className="h-4 w-4 mr-2 text-amber-600" />
                                  Marcar no asistió
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChange(apt, 'cancelled')
                                  }
                                >
                                  <XCircle className="h-4 w-4 mr-2 text-red-600" />
                                  Cancelar
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {apt.status === 'confirmed' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleStatusChange(apt, 'checked_in')
                                  }
                                >
                                  <User className="h-4 w-4 mr-2 text-indigo-600" />
                                  Registrar llegada
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDelete(apt)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t border-ml-outline-variant/10 bg-ml-surface-container-low/30">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ml-[4.5rem]">
                          {apt.reason && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-1">
                                Motivo
                              </p>
                              <p className="text-sm text-ml-on-surface">
                                {apt.reason}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-1">
                              Duracion
                            </p>
                            <p className="text-sm text-ml-on-surface">
                              {apt.duration_minutes} minutos
                            </p>
                          </div>
                          {apt.notes && (
                            <div className="sm:col-span-2">
                              <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-1">
                                Notas
                              </p>
                              <p className="text-sm text-ml-on-surface italic font-headline">
                                {apt.notes}
                              </p>
                            </div>
                          )}
                          {apt.department && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-1">
                                Departamento
                              </p>
                              <p className="text-sm text-ml-on-surface">
                                {apt.department}
                              </p>
                            </div>
                          )}
                          {apt.room_number && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-ml-on-surface-variant/60 font-bold mb-1">
                                Consultorio
                              </p>
                              <p className="text-sm text-ml-on-surface">
                                {apt.room_number}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
